import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Card, {
	CardBody,
	CardHeader,
	CardLabel,
	CardTitle,
	CardActions,
} from '../../components/bootstrap/Card';
import { selectLoginResponse } from '../../redux/login/login.selector';
import { selectDesignationWiseLeaveReportResponse } from '../../redux/NewReport/NewReport.selectors';
import Button from '../../components/bootstrap/Button';
import PageWrapper from '../../layout/PageWrapper/PageWrapper';
import Page from '../../layout/Page/Page';
import SubHeader, { SubHeaderLeft } from '../../layout/SubHeader/SubHeader';
import { getDesignationWiseLeaveReportStart } from '../../redux/NewReport/NewReport.actions';
import Icon from '../../components/icon/Icon';
import TableSpinner from '../../common/table/TableSpinner';
import Input from '../../components/bootstrap/forms/Input';

interface LeaveReportData {
	designation: string;
	[key: string]: string | number; // For dynamic day columns and details
}

interface ReportResponse {
	dates: string[];
	report: LeaveReportData[];
}

interface ReportState {
	reports: {
		designationLeaveReport: {
			dates: string[];
			report: LeaveReportData[];
			error?: string;
		} | null;
	};
}

interface PopupPosition {
	x: number;
	y: number;
}

interface PopupProps {
	details: string;
	onClose: () => void;
	position: PopupPosition;
}

interface PopupState {
	show: boolean;
	details: string;
	position: PopupPosition;
}

const DetailsPopup: React.FC<PopupProps> = ({ details, onClose, position }) => {
	const popupStyle: React.CSSProperties = {
		position: 'fixed',
		top: position.y,
		left: position.x,
		backgroundColor: 'white',
		padding: '12px',
		borderRadius: '8px',
		boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
		zIndex: 1000,
		width: '280px',
		border: '1px solid #e9ecef',
		backdropFilter: 'blur(10px)',
		animation: 'fadeIn 0.2s ease-out',
	};

	// Convert details to array if it's a string, or use empty array if null/undefined
	const detailsList =
		typeof details === 'string' ? details.split(', ') : Array.isArray(details) ? details : [];

	return (
		<>
			<style>
				{`
                    @keyframes fadeIn {
                        from {
                            opacity: 0;
                            transform: translateY(-10px) scale(0.95);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0) scale(1);
                        }
                    }
                `}
			</style>
			<div style={popupStyle}>
				<div
					style={{
						marginBottom: '8px',
						borderBottom: '1px solid #007bff',
						paddingBottom: '6px',
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}>
					<strong style={{ color: '#007bff', fontSize: '13px' }}>Employee Details</strong>
					<Button
						color='primary'
						onClick={onClose}
						size='sm'
						icon='Close'
						className='btn-sm'
						style={{
							padding: '2px 6px',
							fontSize: '10px',
							color: '#007bff',
							backgroundColor: 'transparent',
							border: 'none',
						}}
					/>
				</div>
				<div style={{ maxHeight: '150px', overflowY: 'auto' }}>
					{detailsList.map((detail, index) => (
						<div
							key={index}
							style={{
								marginBottom: '4px',
								padding: '3px 6px',
								backgroundColor: '#f8f9fa',
								borderRadius: '3px',
								borderLeft: '2px solid #007bff',
								fontSize: '11px',
								lineHeight: '1.2',
								whiteSpace: 'nowrap',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
							}}>
							{detail}
						</div>
					))}
				</div>
			</div>
			<div
				onClick={onClose}
				style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }}
			/>
		</>
	);
};

const MONTHS = [
	{ value: '01', label: 'January', short: 'Jan' },
	{ value: '02', label: 'February', short: 'Feb' },
	{ value: '03', label: 'March', short: 'Mar' },
	{ value: '04', label: 'April', short: 'Apr' },
	{ value: '05', label: 'May', short: 'May' },
	{ value: '06', label: 'June', short: 'Jun' },
	{ value: '07', label: 'July', short: 'Jul' },
	{ value: '08', label: 'August', short: 'Aug' },
	{ value: '09', label: 'September', short: 'Sep' },
	{ value: '10', label: 'October', short: 'Oct' },
	{ value: '11', label: 'November', short: 'Nov' },
	{ value: '12', label: 'December', short: 'Dec' },
];

// Fixed to 2025 only
const YEARS = [{ value: '2025', label: '2025' }];

const DesignationWiseLeaveReport = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const loginData = useSelector(selectLoginResponse);
    const reportData = useSelector(selectDesignationWiseLeaveReportResponse);
    const decrypted: ReportResponse | null = (reportData && reportData.data) ? (reportData.data as ReportResponse) : null;

	const [selectedYear, setSelectedYear] = useState('2025'); // Fixed to 2025
	const [isLoading, setIsLoading] = useState(false);
	const [viewMode, setViewMode] = useState<'yearly' | 'monthly'>('yearly'); // Toggle between yearly and monthly
	const [selectedMonth, setSelectedMonth] = useState('01'); // For monthly view
	const [employeeNameFilter, setEmployeeNameFilter] = useState<string>('');
	const [employeeCodeFilter, setEmployeeCodeFilter] = useState<string>('');

	const [popup, setPopup] = useState<PopupState>({
		show: false,
		details: '',
		position: { x: 0, y: 0 },
	});

	// Auto-generate report when view mode or month changes
	useEffect(() => {
		if (loginData) {
			handleGenerateReport();
		}
	}, [viewMode, selectedMonth, selectedYear]);

	const handleGenerateReport = () => {
		// Check different possible paths for organization ID
		let organisationID = null;

		if (loginData?.data?.organisationID) {
			organisationID = loginData.data.organisationID;
		} else if (loginData?.organisationID) {
			organisationID = loginData.organisationID;
		} else if (loginData?.payload?.organisationID) {
			organisationID = loginData.payload.organisationID;
		} else if (loginData?.payload?.data?.organisationID) {
			organisationID = loginData.payload.data.organisationID;
		}

		if (!organisationID) {
			console.error('Organization ID not found in login data');
			navigate('/auth-pages/login');
			return;
		}

		setIsLoading(true);

		if (viewMode === 'yearly') {
			// Send yearly format - backend expects selectedMonth but we send year format
            dispatch(
                getDesignationWiseLeaveReportStart({
					selectedMonth: `${selectedYear}-01`, // Send year with January to indicate yearly report
					selectedYear: selectedYear, // Also send year for backend to understand it's yearly
					organisationID: organisationID,
				}),
			);
		} else {
			// Send monthly format
            dispatch(
                getDesignationWiseLeaveReportStart({
					selectedMonth: `${selectedYear}-${selectedMonth}`, // Send specific month
					organisationID: organisationID,
				}),
			);
		}
	};

	// Stop loading when data is received
	useEffect(() => {
		if (decrypted || reportData === null) {
			setIsLoading(false);
		}
	}, [decrypted, reportData]);

	// Filter data by designation
	const filteredReportData =
		decrypted?.report?.filter((row: any) => {
			if (employeeNameFilter) {
				return row.designation.toLowerCase().includes(employeeNameFilter.toLowerCase());
			}
			return true;
		}) || [];

	// Add keyboard event listener for closing popup
	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape' && popup.show) {
				setPopup({ show: false, details: '', position: { x: 0, y: 0 } });
			}
		};

		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [popup.show]);

	const handleExport = () => {
		if (!decrypted?.report?.length) return;

		const isYearly = viewMode === 'yearly';
		const headers = [
			'Designation',
			...(isYearly
				? MONTHS.map((month) => month.short)
				: decrypted?.dates?.map((date: string) =>
						new Date(date).toLocaleDateString('en-GB', {
							day: '2-digit',
							month: '2-digit',
							year: 'numeric',
						}),
					) || []),
			'Total',
		];
		const csvData = [
			headers.join(','),
			...(decrypted?.report || []).map((row: any) => {
				// Calculate total for this designation
				const designationTotal = (isYearly ? MONTHS : decrypted?.dates || []).reduce(
					(total: number, _: any, index: number) => {
						const key = isYearly ? `month_${index + 1}` : `day_${index + 1}`;
						const value = row[key];
						return total + (typeof value === 'number' ? value : 0);
					},
					0,
				);

				return [
					`"${row.designation}"`,
					...(isYearly
						? MONTHS.map((_, i) => `"${row[`month_${i + 1}`] || ''}"`)
						: decrypted?.dates?.map(
								(_: string, i: number) => `"${row[`day_${i + 1}`] || ''}"`,
							) || []),
					`"${designationTotal}"`,
				].join(',');
			}),
			// Add totals row
			[
				'"Total"',
				...(isYearly
					? MONTHS.map((_, monthIndex) => {
							const monthTotal = (decrypted?.report || []).reduce(
								(total: number, row: any) => {
									const monthKey = `month_${monthIndex + 1}`;
									const value = row[monthKey];
									return total + (typeof value === 'number' ? value : 0);
								},
								0,
							);
							return `"${monthTotal}"`;
						})
					: decrypted?.dates?.map((_: string, dayIndex: number) => {
							const dayTotal = (decrypted?.report || []).reduce((total: number, row: any) => {
								const dayKey = `day_${dayIndex + 1}`;
								const value = row[dayKey];
								return total + (typeof value === 'number' ? value : 0);
							}, 0);
							return `"${dayTotal}"`;
						}) || []),
				`"${(decrypted?.report || []).reduce((total: number, row: any) => {
					return (
						total +
						(isYearly
							? MONTHS.reduce((rowTotal: number, _, monthIndex: number) => {
									const monthKey = `month_${monthIndex + 1}`;
									const value = row[monthKey];
									return rowTotal + (typeof value === 'number' ? value : 0);
								}, 0)
							: (decrypted?.dates || []).reduce(
									(rowTotal: number, _: string, dayIndex: number) => {
										const dayKey = `day_${dayIndex + 1}`;
										const value = row[dayKey];
										return rowTotal + (typeof value === 'number' ? value : 0);
									},
									0,
								))
					);
				}, 0)}"`,
			].join(','),
		].join('\n');

		const blob = new Blob([csvData], { type: 'text/csv' });
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = `designation_wise_${viewMode}_leave_report_${selectedYear}${viewMode === 'monthly' ? `_${selectedMonth}` : ''}.csv`;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		window.URL.revokeObjectURL(url);
	};

	const handleCellClick = (event: React.MouseEvent, details: any) => {
		if (!details) return;

		const rect = event.currentTarget.getBoundingClientRect();
		const viewportHeight = window.innerHeight;
		const popupHeight = 180; // Smaller popup height

		// Calculate position relative to viewport
		const x = Math.min(rect.left, window.innerWidth - 420); // Prevent popup from going off-screen horizontally
		let y = rect.bottom + 5; // Position below the clicked cell

		// Check if popup would go below viewport
		if (y + popupHeight > viewportHeight) {
			// Position above the clicked cell instead
			y = rect.top - popupHeight - 5;
		}

		// Ensure popup doesn't go above viewport
		if (y < 10) {
			y = 10;
		}

		setPopup({
			show: true,
			details: details,
			position: { x, y },
		});
	};

	// Calculate totals for each column
	const calculateColumnTotals = () => {
		if (!decrypted?.report) return [];

		const isYearly = viewMode === 'yearly';
		const columns = isYearly ? MONTHS : decrypted.dates || [];

		return columns.map((_: any, index: number) => {
			const key = isYearly ? `month_${index + 1}` : `day_${index + 1}`;
			return (decrypted?.report || []).reduce((total: number, row: any) => {
				const value = row[key];
				return total + (typeof value === 'number' ? value : 0);
			}, 0);
		});
	};

	const columnTotals = calculateColumnTotals();

	return (
		<PageWrapper>
			<SubHeader className='mt-3'>
				<SubHeaderLeft>
					<CardLabel icon='DonutSmall' iconColor='info'>
						<CardTitle>
							Designation-wise Leave Report -{' '}
							{viewMode === 'yearly' ? 'Annual' : 'Monthly'} View
						</CardTitle>
					</CardLabel>
				</SubHeaderLeft>
			</SubHeader>
			<Page container='fluid'>
				<Card stretch>
					<CardHeader>
						<CardLabel icon='FilterList'>
							<CardTitle>Report Filters</CardTitle>
						</CardLabel>
						<CardActions>
							{((decrypted?.report?.length ?? 0) > 0) && (
								<Button
									color='success'
									size='sm'
									icon='Download'
									onClick={handleExport}>
									Export
								</Button>
							)}
						</CardActions>
					</CardHeader>
					<div className='row h-100 pb-3'>
						<div className='col-lg-12 col-md-12'>
							<div className='row ps-4 pt-4'>
								<div className='col-12'>
									<div className='row align-items-end'>
										<div className='col-md-2'>
											<div className='form-floating mb-3'>
												<select
													className='form-select'
													id='yearSelect'
													value={selectedYear}
													onChange={(e) =>
														setSelectedYear(e.target.value)
													}>
													{YEARS.map((year) => (
														<option key={year.value} value={year.value}>
															{year.label}
														</option>
													))}
												</select>
												<label htmlFor='yearSelect'>Year</label>
											</div>
										</div>
										{viewMode === 'monthly' && (
											<div className='col-md-2'>
												<div className='form-floating mb-3'>
													<select
														className='form-select'
														id='monthSelect'
														value={selectedMonth}
														onChange={(e) =>
															setSelectedMonth(e.target.value)
														}>
														{MONTHS.map((month) => (
															<option
																key={month.value}
																value={month.value}>
																{month.label}
															</option>
														))}
													</select>
													<label htmlFor='monthSelect'>Month</label>
												</div>
											</div>
										)}
										<div className='col-md-4'>
											<div className='mb-3'>
												<div className='btn-group' role='group'>
													<Button
														color={
															viewMode === 'yearly'
																? 'primary'
																: 'light'
														}
														onClick={() => setViewMode('yearly')}
														className='me-2'>
														Yearly View
													</Button>
													<Button
														color={
															viewMode === 'monthly'
																? 'primary'
																: 'light'
														}
														onClick={() => setViewMode('monthly')}>
														Monthly View
													</Button>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>

							{isLoading && (
								<div className='text-center my-5'>
									<TableSpinner />
								</div>
							)}

							{!isLoading && decrypted && decrypted.report && decrypted.report.length > 0 && (
								<>
									<CardBody>
										<div
											className='table-responsive'
											style={{
												width: '100%',
												overflowX: 'auto',
												maxHeight: '70vh',
												overflowY: 'auto',
											}}>
											<table
												className='table table-modern table-hover'
												style={{
													fontSize: '12px',
													width: '100%',
													tableLayout: 'fixed',
												}}>
												<thead
													style={{
														position: 'sticky',
														top: 0,
														backgroundColor: 'white',
														zIndex: 1,
													}}>
													<tr>
														<th
															className='bg-light'
															style={{
																position: 'sticky',
																left: 0,
																backgroundColor: 'white',
																zIndex: 2,
																width: '150px',
																padding: '8px 12px',
																fontSize: '12px',
																fontWeight: 'bold',
																borderBottom: '2px solid #dee2e6',
															}}>
															<div className='d-flex flex-column'>
																<span className='fw-bold mb-1'>
																	Designation
																</span>
																<Input
																	type='text'
																	placeholder='Filter...'
																	size='sm'
																	className='form-control-sm'
																	onChange={(
																		e: React.ChangeEvent<HTMLInputElement>,
																	) =>
																		setEmployeeNameFilter(
																			e.target.value,
																		)
																	}
																	value={employeeNameFilter}
																/>
															</div>
														</th>
														{viewMode === 'yearly'
															? // Yearly view - show months
																MONTHS.map((month, index) => (
																	<th
																		key={month.value}
																		className='bg-light text-center'
																		style={{
																			width: '40px',
																			padding: '6px 4px',
																			fontSize: '11px',
																			fontWeight: 'bold',
																			backgroundColor:
																				'#f8f9fa',
																			borderBottom:
																				'2px solid #dee2e6',
																		}}>
																		{month.short}
																	</th>
																))
															: // Monthly view - show days
																decrypted?.dates?.map(
																	(
																		date: string,
																		index: number,
																	) => (
																		<th
																			key={date}
																			className='bg-light text-center'
																			style={{
																				width: '35px',
																				padding: '6px 2px',
																				fontSize: '11px',
																				fontWeight: 'bold',
																				backgroundColor:
																					'#f8f9fa',
																				borderBottom:
																					'2px solid #dee2e6',
																			}}>
																			{new Date(
																				date,
																			).getDate()}
																		</th>
																	),
																)}
														<th
															className='bg-light text-center'
															style={{
																position: 'sticky',
																right: 0,
																backgroundColor: 'white',
																zIndex: 2,
																width: '60px',
																padding: '8px 12px',
																fontSize: '12px',
																fontWeight: 'bold',
																borderBottom: '2px solid #dee2e6',
															}}>
															Total
														</th>
													</tr>
												</thead>
												<tbody>
													{filteredReportData.map(
														(row: any, rowIndex: number) => {
															const isYearly = viewMode === 'yearly';
															const columns = isYearly
																? MONTHS
																: decrypted?.dates || [];
															const keyPrefix = isYearly
																? 'month_'
																: 'day_';

															// Calculate total for this designation
															const designationTotal = columns.reduce(
																(
																	total: number,
																	_: any,
																	index: number,
																) => {
																	const key = `${keyPrefix}${index + 1}`;
																	const value = row[key];
																	return (
																		total +
																		(typeof value === 'number'
																			? value
																			: 0)
																	);
																},
																0,
															);

															return (
																<tr
																	key={rowIndex}
																	style={{
																		borderBottom:
																			'1px solid #f1f3f4',
																	}}>
																	<td
																		style={{
																			position: 'sticky',
																			left: 0,
																			backgroundColor:
																				'white',
																			zIndex: 1,
																			fontWeight: '500',
																			width: '150px',
																			padding: '8px 12px',
																			fontSize: '12px',
																			borderRight:
																				'1px solid #dee2e6',
																			verticalAlign: 'middle',
																		}}>
																		{row.designation}
																	</td>
																	{columns.map(
																		(
																			item: any,
																			index: number,
																		) => {
																			const key = `${keyPrefix}${index + 1}`;
																			const detailsKey = `details_${index + 1}`;
																			const value = row[key];
																			const details =
																				row[detailsKey];

																			return (
																				<td
																					key={
																						isYearly
																							? item.value
																							: `day-${index}`
																					}
																					style={{
																						textAlign:
																							'center',
																						fontSize:
																							'11px',
																						padding:
																							'6px 2px',
																						width: isYearly
																							? '40px'
																							: '35px',
																						cursor: value
																							? 'pointer'
																							: 'default',
																						backgroundColor:
																							value
																								? '#e3f2fd'
																								: 'transparent',
																						transition:
																							'all 0.2s ease',
																						border: value
																							? '1px solid #2196f3'
																							: '1px solid transparent',
																						borderRadius:
																							value
																								? '4px'
																								: '0',
																						fontWeight:
																							value
																								? '500'
																								: 'normal',
																						verticalAlign:
																							'middle',
																					}}
																					onMouseEnter={(
																						e,
																					) => {
																						if (value) {
																							e.currentTarget.style.backgroundColor =
																								'#bbdefb';
																							e.currentTarget.style.transform =
																								'scale(1.05)';
																						}
																					}}
																					onMouseLeave={(
																						e,
																					) => {
																						if (value) {
																							e.currentTarget.style.backgroundColor =
																								'#e3f2fd';
																							e.currentTarget.style.transform =
																								'scale(1)';
																						}
																					}}
																					onClick={(
																						e,
																					) => {
																						if (
																							value &&
																							details
																						) {
																							handleCellClick(
																								e,
																								details,
																							);
																						}
																					}}>
																					{typeof value ===
																					'number'
																						? value
																						: ''}
																				</td>
																			);
																		},
																	)}
																	<td
																		style={{
																			position: 'sticky',
																			right: 0,
																			backgroundColor:
																				'white',
																			zIndex: 1,
																			fontWeight: 'bold',
																			textAlign: 'center',
																			width: '60px',
																			padding: '8px 12px',
																			fontSize: '12px',
																			borderLeft:
																				'2px solid #dee2e6',
																			verticalAlign: 'middle',
																			color: '#28a745',
																		}}>
																		{designationTotal > 0
																			? designationTotal
																			: ''}
																	</td>
																</tr>
															);
														},
													)}
													{/* Totals Row */}
													<tr
														style={{
															borderTop: '2px solid #dee2e6',
															backgroundColor: '#f8f9fa',
															fontWeight: 'bold',
														}}>
														<td
															style={{
																position: 'sticky',
																left: 0,
																backgroundColor: '#f8f9fa',
																zIndex: 1,
																fontWeight: 'bold',
																width: '150px',
																padding: '8px 12px',
																fontSize: '12px',
																borderRight: '1px solid #dee2e6',
																verticalAlign: 'middle',
																color: '#495057',
															}}>
															Total
														</td>
														{columnTotals.map(
															(total: number, index: number) => (
																<td
																	key={`total-${index}`}
																	style={{
																		textAlign: 'center',
																		fontSize: '11px',
																		padding: '6px 2px',
																		width:
																			viewMode === 'yearly'
																				? '40px'
																				: '35px',
																		fontWeight: 'bold',
																		verticalAlign: 'middle',
																		color: '#28a745',
																		backgroundColor: '#f8f9fa',
																	}}>
																	{total > 0 ? total : ''}
																</td>
															),
														)}
														<td
															style={{
																position: 'sticky',
																right: 0,
																backgroundColor: '#f8f9fa',
																zIndex: 1,
																fontWeight: 'bold',
																textAlign: 'center',
																width: '60px',
																padding: '8px 12px',
																fontSize: '12px',
																borderLeft: '2px solid #dee2e6',
																verticalAlign: 'middle',
																color: '#28a745',
															}}>
															{columnTotals.reduce(
																(sum: number, total: number) =>
																	sum + total,
																0,
															)}
														</td>
													</tr>
												</tbody>
											</table>
										</div>
									</CardBody>
								</>
							)}

							{!isLoading &&
								decrypted &&
								(!decrypted.report || decrypted.report.length === 0) && (
									<div className='alert alert-info m-3'>
										No data available for the selected period
									</div>
								)}
						</div>
					</div>
				</Card>
				{popup.show && (
					<DetailsPopup
						details={popup.details}
						onClose={() =>
							setPopup({ show: false, details: '', position: { x: 0, y: 0 } })
						}
						position={popup.position}
					/>
				)}
			</Page>
		</PageWrapper>
	);
};

export default DesignationWiseLeaveReport;
